/**
 * Turn a Postgres error into something worth reading.
 *
 * Matching on message text is fragile and was actively misleading: a NOT NULL
 * violation on stock_no was being reported as "already used", which sent the
 * person looking for a duplicate that did not exist. Postgres gives a code for
 * exactly this reason — use it.
 *
 * https://www.postgresql.org/docs/current/errcodes-appendix.html
 */

import type { PostgrestError } from "@supabase/supabase-js";

const UNIQUE = "23505";
const NOT_NULL = "23502";
const CHECK = "23514";
const FOREIGN_KEY = "23503";

/** Column and constraint names mapped to what the person calls them. */
const FRIENDLY: Record<string, string> = {
  stock_no: "stock number",
  vin: "VIN",
  deal_no: "deal number",
  receipt_no: "receipt number",
  county_name: "county",
  county: "county",
  email: "email address",
  zip: "ZIP code",
};

const nameOf = (raw: string | undefined) =>
  raw ? FRIENDLY[raw] ?? raw.replace(/_/g, " ") : "a required field";

/** Pull the column out of a NOT NULL message, which names it reliably. */
function columnFromNotNull(message: string): string | undefined {
  return /column "([^"]+)"/.exec(message)?.[1];
}

export function describeDbError(error: PostgrestError | null): string {
  if (!error) return "";

  const msg = error.message ?? "";

  switch (error.code) {
    case UNIQUE: {
      if (msg.includes("vehicles_active_vin_uniq") || msg.includes("vin"))
        return "That VIN is already on the lot.";
      if (msg.includes("stock_no"))
        return "That stock number is already used. Leave it blank and one will be assigned.";
      if (msg.includes("deal_no"))
        return "That deal number is already used.";
      return "Something with that value already exists.";
    }

    case NOT_NULL: {
      const col = columnFromNotNull(msg);
      // The usual cause is a migration that never ran, so say so rather than
      // asking for a value the person deliberately left blank.
      if (col === "stock_no")
        return "The database is not assigning stock numbers. Run " +
               "04_patch_stock_numbers.sql in Supabase, or type a stock number by hand.";
      if (col === "deal_no")
        return "The database is not assigning deal numbers. Run " +
               "05_patch_deal_numbers.sql in Supabase.";
      return `${nameOf(col)} is required.`;
    }

    case CHECK: {
      if (msg.includes("cust_has_contact"))
        return "Add a phone number or an email address.";
      if (msg.includes("cust_fl_needs_county"))
        return "Florida buyers need a county so the surtax can be worked out.";
      if (msg.includes("deal_trade_not_exceeding_price"))
        return "The trade allowance cannot be more than the price.";
      if (msg.includes("deal_warranty_pairs"))
        return "A warranty needs both a term and a mileage cap.";
      if (msg.includes("vin_valid"))
        return "That VIN does not check out.";
      if (msg.includes("year_range"))
        return "That model year is out of range.";
      if (msg.includes("money_nonneg") || msg.includes("nonneg"))
        return "Amounts cannot be negative.";
      if (msg.includes("at least 18"))
        return "Buyer must be at least 18.";
      return `The database rejected that: ${msg}`;
    }

    case FOREIGN_KEY: {
      if (msg.includes("county"))
        return "That county is not loaded. Run 03_seed_counties.sql in Supabase.";
      if (msg.includes("deals_vehicle_id_fkey"))
        return "This vehicle is on a deal, so it can't be deleted. "
             + "Void the deal first if it was a mistake.";
      if (msg.includes("deals_buyer_id_fkey"))
        return "This customer is on a deal, so they can't be deleted. "
             + "Void the deal first.";
      if (msg.includes("deposits"))
        return "There is a deposit against this record, so it can't be deleted.";
      if (msg.includes("documents"))
        return "Printed paperwork is filed against this record, so it can't be deleted.";
      return "Something else still refers to this record, so it can't be removed.";
    }

    default:
      return `The database rejected this: ${msg}`;
  }
}
